import { Component, computed, inject, input, output, signal, ViewContainerRef } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule } from '@ngx-translate/core';
import { MatDividerModule } from '@angular/material/divider';
import { AiCapabilityForm, AiCapabilityFormArray, AiToolForm } from '../../../core/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AiCapabilitySelectComponent } from '../ai-capability-select-dialog/ai-capability-select-dialog.component';
import { GoalInterface, PromptModuleKey, PromptModuleApiService } from '@vendasta/ai-assistants';
import { AiAssistantService } from '../../../core/services/ai-assistant.service';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MatMenuModule } from '@angular/material/menu';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { EnableLeadCaptureDialogComponent } from '../../ai-assistant-configuration/enable-lead-capture-dialog/enable-lead-capture-dialog.component';
import { HIDDEN_GOAL_IDS } from '../../../core/ai-assistant.constants';
import { AiAssistantFormV2Service } from '../../ai-assistant-configuration/ai-assistant-form-v2/ai-assistant-form-v2.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { BadgeComponent } from '@vendasta/galaxy/badge';
import {
  AiCapabilityCustomForms,
  AiCapabilityFormLoaderComponent,
} from '../ai-capability-form-loader/ai-capability-form-loader.component';

@Component({
  selector: 'ai-capabilities-list',
  imports: [
    MatMenuModule,
    MatBadgeModule,
    MatCardModule,
    MatDividerModule,
    MatIconModule,
    MatButtonModule,
    MatIconButton,
    MatDialogModule,
    TranslateModule,
    BadgeComponent,
    AiCapabilityFormLoaderComponent,
  ],
  templateUrl: './ai-capabilities-list.component.html',
  styleUrl: './ai-capabilities-list.component.scss',
  host: {
    class: 'ai-capabilities-list',
  },
})
export class AiCapabilitiesListComponent {
  readonly capabilities = input.required<AiCapabilityFormArray>();
  readonly capabilityFormDisplay = output<AiCapabilityForm | undefined>();
  readonly rollback = output<AiCapabilityForm>();
  readonly toolFormDisplay = output<{ parentCapabilityForm?: AiCapabilityForm; form: AiToolForm } | undefined>();

  readonly dialog = inject(MatDialog);
  private readonly assistantService = inject(AiAssistantService);
  private readonly aiAssistantServiceV2 = inject(AiAssistantFormV2Service);
  private readonly promptModuleApiService = inject(PromptModuleApiService);
  private readonly snackbar = inject(SnackbarService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly confirmationModal = inject(OpenConfirmationModalService);

  protected readonly learnMoreComponents = new Map(
    Object.entries({
      DefaultLeadCapture: EnableLeadCaptureDialogComponent,
    }),
  );

  readonly assistant = toSignal(this.aiAssistantServiceV2.aiAssistant$);

  readonly AiCapabilityCustomForms = AiCapabilityCustomForms;

  private readonly capabilitiesUpdateTrigger = signal(0);

  private readonly visibleCapabilityIndices = computed(() => {
    // Include a trigger to force recalculation when capabilities are modified
    this.capabilitiesUpdateTrigger();

    const formArray = this.capabilities();
    const visibleIndices: number[] = [];

    for (let i = 0; i < formArray.controls.length; i++) {
      if (!formArray.at(i).isHidden()) {
        visibleIndices.push(i);
      }
    }

    return visibleIndices;
  });

  handleCapabilityClicked(capability: AiCapabilityForm, event: Event) {
    event.stopPropagation();
    this.capabilityFormDisplay.emit(capability);
  }

  handleCapabilityRemoveClicked(event: Event, i: number) {
    event.stopPropagation();
    this.confirmationModal
      .openModal({
        type: 'warn',
        title: 'AI_ASSISTANT.SETTINGS.REMOVE_CAPABILITY_TITLE',
        message: 'AI_ASSISTANT.SETTINGS.REMOVE_CAPABILITY_CONFIRMATION',
        confirmButtonText: 'AI_ASSISTANT.SETTINGS.REMOVE',
      })
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.capabilities().removeAt(i);
          this.capabilitiesUpdateTrigger.update((trigger) => trigger + 1);
        }
      });
  }

  handleLearnMoreClicked(capabilityID: string, event: Event) {
    event.stopPropagation();
    if (this.learnMoreComponents.has(capabilityID)) {
      const learnMoreComponent = this.learnMoreComponents.get(capabilityID);
      if (learnMoreComponent) {
        this.dialog.open(learnMoreComponent, {
          data: { source: 'capabilities-list' },
        });
      }
    }
  }

  capabilityClickable(capability: AiCapabilityForm): boolean {
    const goalId = capability.controls.goalId.getRawValue() || '';
    const customForm = this.AiCapabilityCustomForms[goalId];

    return !this.hasCustomForm(capability) && !(goalId in this.AiCapabilityCustomForms && customForm === null);
  }

  hasCustomForm(capability: AiCapabilityForm): boolean {
    return !!this.AiCapabilityCustomForms[capability.controls.goalId.getRawValue() || ''];
  }

  async handleCapabilityAddClicked() {
    const dialogRef = this.dialog.open(AiCapabilitySelectComponent, {
      viewContainerRef: this.viewContainerRef,
      width: '900px',
      height: '600px',
      data: {
        assistant: this.assistant(),
        excludedGoalIds: [
          ...this.capabilities()
            .controls.map((capabilityForm) => capabilityForm.controls.goalId.value)
            .filter((id) => id != null),
          ...this.capabilities()
            .controls.map((capabilityForm) => capabilityForm.controls.overrideOf.getRawValue()?.id)
            .filter((id) => !!id),
          ...HIDDEN_GOAL_IDS,
        ],
      },
    });

    dialogRef.afterClosed().subscribe(async (result: { goal?: GoalInterface; newGoal: boolean } | '') => {
      if (result === '' || !result) {
        return;
      }

      const { goal: chosenGoal, newGoal } = result;

      if (chosenGoal) {
        let promptModuleContents: Record<string, string> = {};
        const promptModuleKeys =
          chosenGoal?.promptModules?.map((pm) => new PromptModuleKey({ id: pm.id, namespace: pm.namespace })) || [];
        if (promptModuleKeys.length > 0) {
          try {
            promptModuleContents = await this.assistantService.getMultiPromptModuleVersions(promptModuleKeys);
          } catch (err) {
            this.snackbar.openErrorSnack('AI_ASSISTANT.GOALS.ADD_GOAL.ERROR_LOADING_CAPABILITY');
            return;
          }
        }
        const newCapabilityForm = new AiCapabilityForm(
          this.promptModuleApiService,
          { goal: chosenGoal, configuration: [] },
          promptModuleContents,
        );
        this.capabilities().push(newCapabilityForm);
        this.capabilitiesUpdateTrigger.update((trigger) => trigger + 1);
      } else if (newGoal) {
        const newCapabilityForm = new AiCapabilityForm(this.promptModuleApiService);
        // Don't add to capabilities array yet - wait for "Done" to be clicked
        // Auto-display the new capability form
        this.capabilityFormDisplay.emit(newCapabilityForm);
        this.capabilitiesUpdateTrigger.update((trigger) => trigger + 1);
      }
    });
  }

  moveCapability(fromIndex: number, toIndex: number): void {
    const formArray = this.capabilities();

    // Don't move if indices are invalid
    if (toIndex < 0 || toIndex >= formArray.controls.length) {
      return;
    }

    const control = formArray.at(fromIndex);
    formArray.removeAt(fromIndex);
    formArray.insert(toIndex, control);
    this.capabilitiesUpdateTrigger.update((trigger) => trigger + 1);
  }

  private getPreviousVisibleIndex(currentIndex: number): number | null {
    const visible = this.visibleCapabilityIndices();
    const position = visible.indexOf(currentIndex);
    return position > 0 ? visible[position - 1] : null;
  }

  private getNextVisibleIndex(currentIndex: number): number | null {
    const visible = this.visibleCapabilityIndices();
    const position = visible.indexOf(currentIndex);
    return position >= 0 && position < visible.length - 1 ? visible[position + 1] : null;
  }

  /**
   * Moves a capability up to the position above the first visible capability above it
   */
  moveCapabilityUp(index: number): void {
    const previousVisibleIndex = this.getPreviousVisibleIndex(index);

    if (previousVisibleIndex !== null) {
      this.moveCapability(index, previousVisibleIndex);
    }
  }

  /**
   * Moves a capability down to the position below the first visible capability below it
   */
  moveCapabilityDown(index: number): void {
    const nextVisibleIndex = this.getNextVisibleIndex(index);

    if (nextVisibleIndex !== null) {
      this.moveCapability(index, nextVisibleIndex);
    }
  }

  isMoveUpDisabled(index: number): boolean {
    const visible = this.visibleCapabilityIndices();
    return visible.length > 0 && visible[0] === index;
  }

  isMoveDownDisabled(index: number): boolean {
    const visible = this.visibleCapabilityIndices();
    return visible.length > 0 && visible[visible.length - 1] === index;
  }

  handleRollbackClicked(capability: AiCapabilityForm, event: Event) {
    event.stopPropagation();
    this.rollback.emit(capability);
  }
}
